import { type DragEventHandler, type ReactNode, useRef, useState } from 'react';
import type { Control, UseFormRegister } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { motion, MotionConfig } from 'framer-motion';
import { Check, Eye, EyeOff, FileText, IdCard, LockKeyhole, Mail, Upload, User, X } from 'lucide-react';
import type { Value as PhoneValue } from 'react-phone-number-input';

import {
  Checkbox,
  DatePicker,
  FieldError,
  Input,
  PhoneInput,
  Select,
  SignatureInput,
  Textarea,
} from '@/shared/ui';
import { cn } from '@/shared/utils/cn';
import { useToday } from '@/shared/hooks/useToday';

import type {
  FormFieldConfig,
  FormFieldIcon,
  FormSubmitValues,
  FormWidgetConfig,
} from './form-widget.types';
import type { CommonInputProps } from './ConfigurableFieldShell';
import { FieldShell } from './ConfigurableFieldShell';
import { ConfigurableReadOnlyField } from './ConfigurableReadOnlyField';
import { getDateBoundsForAge } from './date-field';
import { getFieldContainerClass, getFieldDisplay, getFieldId } from './form-display';
import { getFieldOptions } from './form-visibility';
import { formatIdentificationInput } from './identification';
import { PASSWORD_RULES } from './password-rules';

const INPUT_TYPE_BY_FIELD: Partial<Record<FormFieldConfig['type'], string>> = {
  email: 'email',
  url: 'url',
};

const FIELD_ICON: Record<FormFieldIcon, typeof Mail> = {
  mail: Mail,
  lock: LockKeyhole,
  user: User,
  id: IdCard,
};

/** El icono acompaña al input sin robarle el clic ni el espacio del texto. */
function InputWithIcon({ children, icon }: { children: ReactNode; icon?: FormFieldIcon }) {
  if (!icon) return <>{children}</>;

  const Icon = FIELD_ICON[icon];

  return (
    <div className="relative">
      <span className="text-adipa-text-secondary pointer-events-none absolute inset-y-0 left-3 flex items-center">
        <Icon className="h-4 w-4" aria-hidden="true" />
      </span>
      {children}
    </div>
  );
}

type ConfigurableFormFieldProps = {
  config: FormWidgetConfig;
  control: Control<FormSubmitValues>;
  effectiveValues: FormSubmitValues;
  error?: string;
  field: FormFieldConfig;
  isAccountField: boolean;
  register: UseFormRegister<FormSubmitValues>;
};

type BaseFieldProps = {
  commonInputProps: CommonInputProps;
  config: FormWidgetConfig;
  control: Control<FormSubmitValues>;
  error?: string;
  field: FormFieldConfig;
  fieldDisplay: ReturnType<typeof getFieldDisplay>;
  fieldId: string;
  register: UseFormRegister<FormSubmitValues>;
};

export function ConfigurableFormField({
  config,
  control,
  effectiveValues,
  error,
  field,
  isAccountField,
  register,
}: ConfigurableFormFieldProps) {
  const fieldId = getFieldId(config.formId, field.name);
  const fieldDisplay = getFieldDisplay(field, effectiveValues);
  const commonInputProps = {
    id: fieldId,
    hasError: Boolean(error),
    'aria-describedby': `${fieldId}-error`,
  };
  const isReadOnlyField = field.type === 'readonly' || isAccountField;

  if (isReadOnlyField) {
    return (
      <ConfigurableReadOnlyField
        commonInputProps={commonInputProps}
        config={config}
        error={error}
        field={field}
        fieldDisplay={fieldDisplay}
        fieldId={fieldId}
        register={register}
        value={effectiveValues[field.name]}
      />
    );
  }

  const baseFieldProps = {
    commonInputProps,
    config,
    control,
    error,
    field,
    fieldDisplay,
    fieldId,
    register,
  };

  if (field.type === 'phone') {
    return <PhoneField {...baseFieldProps} />;
  }

  if (field.type === 'select') {
    return <SelectField {...baseFieldProps} effectiveValues={effectiveValues} />;
  }

  if (field.type === 'textarea') {
    return <TextareaField {...baseFieldProps} />;
  }

  if (field.type === 'checkbox') {
    return <CheckboxField {...baseFieldProps} />;
  }

  if (field.type === 'password') {
    return <PasswordField {...baseFieldProps} effectiveValues={effectiveValues} />;
  }

  if (field.type === 'date') {
    return <DateField {...baseFieldProps} />;
  }

  if (field.type === 'file') {
    return <FileField {...baseFieldProps} />;
  }

  if (field.type === 'signature') {
    return <SignatureField {...baseFieldProps} />;
  }

  if (field.validation?.type === 'identification') {
    return <IdentificationField {...baseFieldProps} effectiveValues={effectiveValues} />;
  }

  return <TextField {...baseFieldProps} />;
}

function PhoneField({ config, control, error, field, fieldDisplay, fieldId }: BaseFieldProps) {
  return (
    <FieldShell error={error} field={field} fieldDisplay={fieldDisplay} fieldId={fieldId}>
      <Controller
        name={field.name}
        control={control}
        render={({ field: controllerField }) => (
          <PhoneInput
            id={fieldId}
            value={controllerField.value as PhoneValue | undefined}
            onChange={(value) => controllerField.onChange(value ?? '')}
            onBlur={controllerField.onBlur}
            defaultCountry={config.country}
            hasError={Boolean(error)}
            autoComplete={field.autocomplete ?? 'tel'}
            aria-describedby={`${fieldId}-error`}
          />
        )}
      />
    </FieldShell>
  );
}

function SelectField({
  commonInputProps,
  control,
  effectiveValues,
  error,
  field,
  fieldDisplay,
  fieldId,
}: BaseFieldProps & {
  effectiveValues: FormSubmitValues;
}) {
  const options = getFieldOptions(field, effectiveValues);
  return (
    <FieldShell error={error} field={field} fieldDisplay={fieldDisplay} fieldId={fieldId}>
      <Controller
        name={field.name}
        control={control}
        render={({ field: controllerField }) => (
          <Select
            {...commonInputProps}
            name={controllerField.name}
            value={typeof controllerField.value === 'string' ? controllerField.value : ''}
            onValueChange={controllerField.onChange}
            onBlur={controllerField.onBlur}
            placeholder={fieldDisplay.placeholder}
            options={options}
          />
        )}
      />
    </FieldShell>
  );
}

function TextareaField({
  commonInputProps,
  error,
  field,
  fieldDisplay,
  fieldId,
  register,
}: BaseFieldProps) {
  return (
    <FieldShell error={error} field={field} fieldDisplay={fieldDisplay} fieldId={fieldId}>
      <Textarea
        {...commonInputProps}
        placeholder={fieldDisplay.placeholder}
        autoComplete={field.autocomplete}
        {...register(field.name)}
      />
    </FieldShell>
  );
}

function CheckboxField({ error, field, fieldDisplay, fieldId, register }: BaseFieldProps) {
  return (
    <div className={cn('flex flex-col gap-1', getFieldContainerClass(field))}>
      <Checkbox
        id={fieldId}
        hasError={Boolean(error)}
        aria-describedby={`${fieldId}-error`}
        label={fieldDisplay.label}
        {...register(field.name)}
      />
      <FieldError id={`${fieldId}-error`} message={error} />
    </div>
  );
}

function IdentificationField({
  commonInputProps,
  control,
  effectiveValues,
  error,
  field,
  fieldDisplay,
  fieldId,
}: BaseFieldProps & {
  effectiveValues: FormSubmitValues;
}) {
  const validation = field.validation?.type === 'identification' ? field.validation : undefined;

  return (
    <FieldShell error={error} field={field} fieldDisplay={fieldDisplay} fieldId={fieldId}>
      <Controller
        name={field.name}
        control={control}
        render={({ field: controllerField }) => (
          <InputWithIcon icon={field.icon}>
            <Input
              key={`${field.name}-identification`}
              {...commonInputProps}
              type="text"
              value={typeof controllerField.value === 'string' ? controllerField.value : ''}
              placeholder={fieldDisplay.placeholder}
              inputMode={fieldDisplay.inputMode}
              readOnly={field.type === 'readonly'}
              autoComplete={field.autocomplete}
              className={field.icon ? 'pl-10' : undefined}
              onBlur={controllerField.onBlur}
              onChange={(event) => {
                const country = validation ? effectiveValues[validation.countryField] : undefined;
                controllerField.onChange(formatIdentificationInput(country, event.target.value));
              }}
            />
          </InputWithIcon>
        )}
      />
    </FieldShell>
  );
}

function TextField({
  commonInputProps,
  error,
  field,
  fieldDisplay,
  fieldId,
  register,
}: BaseFieldProps) {
  return (
    <FieldShell error={error} field={field} fieldDisplay={fieldDisplay} fieldId={fieldId}>
      <InputWithIcon icon={field.icon}>
        <Input
          key={`${field.name}-editable`}
          {...commonInputProps}
          type={INPUT_TYPE_BY_FIELD[field.type] ?? 'text'}
          placeholder={fieldDisplay.placeholder}
          inputMode={fieldDisplay.inputMode}
          readOnly={field.type === 'readonly'}
          autoComplete={field.autocomplete}
          className={field.icon ? 'pl-10' : undefined}
          {...register(field.name)}
        />
      </InputWithIcon>
    </FieldShell>
  );
}

function PasswordField({
  commonInputProps,
  effectiveValues,
  error,
  field,
  fieldDisplay,
  fieldId,
  register,
}: BaseFieldProps & {
  effectiveValues: FormSubmitValues;
}) {
  const [isVisible, setIsVisible] = useState(false);
  const value = typeof effectiveValues[field.name] === 'string' ? (effectiveValues[field.name] as string) : '';
  const showRules = Boolean(field.password?.rules);
  const rulesId = `${fieldId}-rules`;

  return (
    <FieldShell error={error} field={field} fieldDisplay={fieldDisplay} fieldId={fieldId}>
      <div className="relative">
        <InputWithIcon icon={field.icon}>
          <Input
            {...commonInputProps}
            aria-describedby={showRules ? `${rulesId} ${fieldId}-error` : `${fieldId}-error`}
            type={isVisible ? 'text' : 'password'}
            placeholder={fieldDisplay.placeholder}
            autoComplete={field.autocomplete ?? 'new-password'}
            className={field.icon ? 'px-10' : 'pr-10'}
            {...register(field.name)}
          />
        </InputWithIcon>
        <button
          type="button"
          className="text-adipa-text-secondary duration-adipa-fast hover:text-adipa-primary focus-visible:ring-adipa-focus-ring absolute inset-y-0 right-3 flex items-center transition-colors focus-visible:outline-none focus-visible:ring-2"
          onClick={() => setIsVisible((current) => !current)}
          aria-label={isVisible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
          aria-pressed={isVisible}
        >
          {isVisible ? (
            <EyeOff className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Eye className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
      </div>
      {showRules && (
        <ul id={rulesId} className="mt-1 grid gap-1 text-xs" aria-label="Requisitos de la contraseña">
          {PASSWORD_RULES.map((rule) => {
            const passed = value.length > 0 && rule.test(value);
            return (
              <li
                key={rule.id}
                className={cn(
                  'flex items-center gap-1.5',
                  passed ? 'text-adipa-success' : 'text-adipa-text-secondary',
                )}
              >
                <Check
                  className={cn('h-3.5 w-3.5 shrink-0', !passed && 'opacity-40')}
                  aria-hidden="true"
                />
                <span>{rule.label}</span>
                <span className="sr-only">{passed ? ' (cumplido)' : ' (pendiente)'}</span>
              </li>
            );
          })}
        </ul>
      )}
    </FieldShell>
  );
}

function DateField({ commonInputProps, control, error, field, fieldDisplay, fieldId }: BaseFieldProps) {
  const today = useToday();
  const { minDate, maxDate } = today ? getDateBoundsForAge(field.date ?? {}, today) : {};

  return (
    <FieldShell error={error} field={field} fieldDisplay={fieldDisplay} fieldId={fieldId}>
      <Controller
        name={field.name}
        control={control}
        render={({ field: controllerField }) => (
          <DatePicker
            id={fieldId}
            name={controllerField.name}
            value={typeof controllerField.value === 'string' ? controllerField.value : ''}
            onValueChange={controllerField.onChange}
            onBlur={controllerField.onBlur}
            hasError={commonInputProps.hasError}
            placeholder={fieldDisplay.placeholder}
            minDate={minDate}
            maxDate={maxDate}
            aria-describedby={commonInputProps['aria-describedby']}
          />
        )}
      />
    </FieldShell>
  );
}

const BYTES_PER_KB = 1024;
const BYTES_PER_MB = BYTES_PER_KB * 1024;

function formatFileSize(bytes: number) {
  if (bytes < BYTES_PER_MB) return `${Math.max(1, Math.round(bytes / BYTES_PER_KB))} KB`;
  const megabytes = bytes / BYTES_PER_MB;
  return `${Number.isInteger(megabytes) ? megabytes : megabytes.toFixed(1)} MB`;
}

function SignatureField({ control, error, field, fieldDisplay, fieldId }: BaseFieldProps) {
  return (
    <FieldShell error={error} field={field} fieldDisplay={fieldDisplay} fieldId={fieldId}>
      <Controller
        name={field.name}
        control={control}
        render={({ field: controllerField }) => (
          <SignatureInput
            id={fieldId}
            value={controllerField.value instanceof File ? controllerField.value : undefined}
            hasError={Boolean(error)}
            aria-describedby={`${fieldId}-error`}
            focusRef={controllerField.ref}
            onChange={(file) => {
              controllerField.onChange(file ?? '');
              if (file) controllerField.onBlur();
            }}
          />
        )}
      />
    </FieldShell>
  );
}

const FILE_CARD_VARIANTS = {
  initial: { x: 0, y: 0, opacity: 1 },
  animate: { x: 16, y: -16, opacity: 0.95 },
};

const FILE_GHOST_VARIANTS = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
};

const FILE_CARD_TRANSITION = { type: 'spring', stiffness: 300, damping: 20 } as const;

type FileDropHandlers = {
  onDragEnter: DragEventHandler<HTMLElement>;
  onDragOver: DragEventHandler<HTMLElement>;
  onDragLeave: DragEventHandler<HTMLElement>;
  onDrop: DragEventHandler<HTMLElement>;
};

function getFileExtension(file: File) {
  return file.name.includes('.') ? (file.name.split('.').pop() ?? '') : '';
}

type FileDropzoneProps = {
  dropHandlers: FileDropHandlers;
  error?: string;
  fieldId: string;
  hint: string;
  input: ReactNode;
  isDragging: boolean;
  maxLabel?: string;
};

function FileDropzone({ dropHandlers, error, fieldId, hint, input, isDragging, maxLabel }: FileDropzoneProps) {
  return (
    <label
      {...dropHandlers}
      htmlFor={fieldId}
      className={cn(
        'rounded-adipa-md duration-adipa-fast bg-adipa-surface-muted relative block cursor-pointer overflow-hidden border border-dashed px-4 py-6 transition-colors sm:px-6',
        'has-[:focus-visible]:border-adipa-primary has-[:focus-visible]:ring-adipa-focus-ring has-[:focus-visible]:ring-[3px]',
        isDragging && 'border-adipa-primary bg-adipa-primary/10',
        !isDragging && (error ? 'border-adipa-error' : 'border-adipa-primary/30 hover:border-adipa-primary/60'),
      )}
    >
      {input}
      <motion.div
        initial="initial"
        animate={isDragging ? 'animate' : 'initial'}
        whileHover="animate"
        className="relative flex flex-col items-center text-center"
      >
        <p className="text-adipa-text-primary relative z-20 text-sm font-semibold">
          {isDragging ? 'Suelta el archivo aquí' : `Arrastra tu archivo aquí o ${hint}`}
        </p>
        {maxLabel && <p className="text-adipa-text-secondary relative z-20 mt-1 text-xs">{maxLabel}</p>}
        <div className="relative mx-auto mt-6 h-24 w-full max-w-32">
          <motion.div
            layoutId={`${fieldId}-file-card`}
            variants={FILE_CARD_VARIANTS}
            transition={FILE_CARD_TRANSITION}
            className="bg-adipa-surface shadow-adipa-card rounded-adipa-md relative z-40 flex h-24 w-full items-center justify-center"
          >
            <Upload className="text-adipa-primary h-5 w-5" aria-hidden="true" />
          </motion.div>
          <motion.div
            variants={FILE_GHOST_VARIANTS}
            className="border-adipa-primary rounded-adipa-md absolute inset-0 z-30 border border-dashed"
          />
        </div>
      </motion.div>
    </label>
  );
}

type FileCardProps = {
  dropHandlers: FileDropHandlers;
  fieldId: string;
  file: File;
  input: ReactNode;
  isDragging: boolean;
  onClear: () => void;
};

function FileCard({ dropHandlers, fieldId, file, input, isDragging, onClear }: FileCardProps) {
  const extension = getFileExtension(file);

  return (
    <div
      {...dropHandlers}
      className={cn(
        'rounded-adipa-md duration-adipa-fast border border-dashed p-1.5 transition-colors',
        isDragging ? 'border-adipa-primary bg-adipa-primary/5' : 'border-transparent',
      )}
    >
      {input}
      <motion.div
        layoutId={`${fieldId}-file-card`}
        transition={FILE_CARD_TRANSITION}
        className="bg-adipa-surface shadow-adipa-card rounded-adipa-md relative z-40 flex w-full items-center gap-3 p-4"
      >
        <FileText className="text-adipa-primary h-6 w-6 shrink-0" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <motion.p
            layout
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-adipa-text-primary truncate text-sm font-semibold"
          >
            {isDragging ? 'Suelta el archivo para reemplazarlo' : file.name}
          </motion.p>
          <motion.p
            layout
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-adipa-text-secondary mt-1 flex flex-wrap items-center gap-2 text-xs"
          >
            {extension && (
              <span className="bg-adipa-surface-muted rounded-adipa-sm px-1.5 py-0.5 font-medium uppercase">
                {extension}
              </span>
            )}
            <span>{formatFileSize(file.size)}</span>
          </motion.p>
        </div>
        <button
          type="button"
          onClick={onClear}
          aria-label={`Quitar ${file.name}`}
          className="text-adipa-text-secondary duration-adipa-fast hover:bg-adipa-surface-muted hover:text-adipa-text-primary focus-visible:ring-adipa-focus-ring flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </motion.div>
    </div>
  );
}

function FileField({ control, error, field, fieldDisplay, fieldId }: BaseFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const maxLabel = field.file?.maxBytes ? `Máximo ${formatFileSize(field.file.maxBytes)}` : undefined;
  const hint = fieldDisplay.placeholder ?? 'elígelo desde tu equipo';

  return (
    <FieldShell error={error} field={field} fieldDisplay={fieldDisplay} fieldId={fieldId}>
      <Controller
        name={field.name}
        control={control}
        render={({ field: controllerField }) => {
          const file = controllerField.value instanceof File ? controllerField.value : undefined;

          const select = (next: File | undefined) => {
            controllerField.onChange(next ?? '');
            controllerField.onBlur();
          };

          const clear = () => {
            select(undefined);
            if (inputRef.current) inputRef.current.value = '';
            inputRef.current?.focus();
          };

          const dropHandlers: FileDropHandlers = {
            onDragEnter: (event) => {
              event.preventDefault();
              setIsDragging(true);
            },
            onDragOver: (event) => {
              event.preventDefault();
              event.dataTransfer.dropEffect = 'copy';
            },
            onDragLeave: (event) => {
              if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
              setIsDragging(false);
            },
            onDrop: (event) => {
              event.preventDefault();
              setIsDragging(false);
              const dropped = event.dataTransfer.files?.[0];
              if (dropped) select(dropped);
            },
          };

          const input = (
            <input
              ref={(element) => {
                inputRef.current = element;
                controllerField.ref(element);
              }}
              id={fieldId}
              name={controllerField.name}
              type="file"
              accept={field.file?.accept}
              aria-invalid={error ? 'true' : undefined}
              aria-describedby={`${fieldId}-error`}
              className="peer sr-only"
              onChange={(event) => select(event.target.files?.[0])}
            />
          );

          return (
            <MotionConfig reducedMotion="user">
              {file ? (
                <FileCard
                  dropHandlers={dropHandlers}
                  fieldId={fieldId}
                  file={file}
                  input={input}
                  isDragging={isDragging}
                  onClear={clear}
                />
              ) : (
                <FileDropzone
                  dropHandlers={dropHandlers}
                  error={error}
                  fieldId={fieldId}
                  hint={hint}
                  input={input}
                  isDragging={isDragging}
                  maxLabel={maxLabel}
                />
              )}
            </MotionConfig>
          );
        }}
      />
    </FieldShell>
  );
}
